import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  UserCheck,
  UserX,
  Trash2,
  Shield,
  ChevronRight,
  Search,
  RefreshCw,
  Mail,
  Calendar,
  UserCog
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import api, { superAdminAPI } from '../../utils/api';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';

const STATUS_FILTERS = ['all', 'active', 'inactive'];

const SuperAdminUsers = () => {
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();

  const [admins, setAdmins] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedAdmin, setExpandedAdmin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const initialLoadDoneRef = useRef(false);

  const fetchAdmins = useCallback(async ({ q = '', status = 'all', withLoading = false } = {}) => {
    try {
      if (withLoading) {
        setLoading(true);
      }
      const response = await superAdminAPI.getAdmins({
        q,
        status,
        limit: 200,
        offset: 0
      });

      const payload = response.data || {};
      const nextAdmins = Array.isArray(payload) ? payload : payload.admins || [];
      setAdmins(nextAdmins);
      setMeta(payload.meta || { total: nextAdmins.length });
    } catch (error) {
      console.error('Error loading super admin users:', error);
      toast.error(t('superadmin.errors.loadAdmins', 'Error loading administrators'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAdmins({
      q: searchQuery.trim(),
      status: filterStatus,
      withLoading: true
    }).finally(() => {
      initialLoadDoneRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    const timeout = setTimeout(() => {
      fetchAdmins({
        q: searchQuery.trim(),
        status: filterStatus
      });
    }, 250);

    return () => clearTimeout(timeout);
  }, [fetchAdmins, filterStatus, searchQuery]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAdmins({
      q: searchQuery.trim(),
      status: filterStatus
    });
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await api.put(`/superadmin/users/${user.id}/toggle-status`, {
        is_verified: !user.is_verified
      });

      toast.success(t('superadmin.success.statusUpdated', 'User status updated'));
      fetchAdmins({
        q: searchQuery.trim(),
        status: filterStatus
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(t('superadmin.errors.updateStatus', 'Error updating user status'));
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmed = await confirm({
      title: t('superadmin.users.deleteConfirmTitle', 'Delete user'),
      message: t(
        'superadmin.users.deleteConfirmMessage',
        'Are you sure you want to delete {{userName}}? This action is permanent.'
      ).replace('{{userName}}', user.name || user.email),
      type: 'danger',
      confirmText: t('common.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel')
    });

    if (!confirmed) return;

    try {
      await api.delete(`/superadmin/users/${user.id}`);

      toast.success(t('superadmin.success.userDeleted', 'User deleted successfully'));
      fetchAdmins({
        q: searchQuery.trim(),
        status: filterStatus
      });
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        t('superadmin.errors.deleteUser', 'Error deleting user');
      toast.error(message);
    }
  };

  const stats = useMemo(() => {
    const totalAdmins = admins.length;
    const activeAdmins = admins.filter((admin) => admin.is_verified).length;
    const totalRh = admins.reduce((sum, admin) => sum + (admin.rh_users_count || admin.rh_users?.length || 0), 0);
    const activeRh = admins.reduce(
      (sum, admin) => sum + (admin.rh_users_active_count || admin.rh_users?.filter((rh) => rh.is_verified).length || 0),
      0
    );

    return {
      totalAdmins,
      activeAdmins,
      totalRh,
      activeRh
    };
  }, [admins]);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-[70vh]">
          <div className="flex items-center gap-3 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" />
          {t('superadmin.users.title', 'Users Management')}
        </h1>
        <p className="text-sm text-gray-600">
          {t('superadmin.users.subtitle', 'Manage administrators and their HR collaborators')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.users.stats.admins', 'Admins')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.totalAdmins}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.users.stats.activeAdmins', 'Active admins')}</div>
          <div className="text-2xl font-semibold text-emerald-600 mt-1">{stats.activeAdmins}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.users.stats.rhUsers', 'HR users')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.totalRh}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.users.stats.activeRh', 'Active HR')}</div>
          <div className="text-2xl font-semibold text-emerald-600 mt-1">{stats.activeRh}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('superadmin.users.searchPlaceholder', 'Search by name or email')}
              className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
              aria-label={t('superadmin.users.searchPlaceholder', 'Search by name or email')}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setFilterStatus(status)}
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  filterStatus === status
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-200 text-gray-600 hover:border-indigo-200'
                }`}
              >
                {status === 'all' && t('common.all', 'All')}
                {status === 'active' && t('common.active', 'Active')}
                {status === 'inactive' && t('common.inactive', 'Inactive')}
              </button>
            ))}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-indigo-200 inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Refresh')}
            </button>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500">
          {t('superadmin.users.resultsCount', 'Results')}: {meta.total || admins.length}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {admins.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            {t('superadmin.users.noResults', 'No administrators found')}
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {admins.map((admin) => {
                const isExpanded = expandedAdmin === admin.id;
                const rhUsers = admin.rh_users || [];
                const canDeleteAdmin = (admin.rh_users_count || rhUsers.length || 0) === 0;

                return (
                  <motion.div
                    key={admin.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="border border-gray-200 rounded-lg"
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedAdmin(isExpanded ? null : admin.id)}
                      className="w-full p-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                            <UserCog className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {admin.name || admin.email}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 mt-1">
                              <span className="inline-flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {admin.email}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(admin.created_at)}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {(admin.rh_users_count || rhUsers.length || 0)} RH
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                              admin.is_verified
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {admin.is_verified ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                          </span>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleUserStatus(admin);
                            }}
                            className={`p-1.5 rounded-lg ${
                              admin.is_verified
                                ? 'text-emerald-600 hover:bg-emerald-50'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            aria-label={admin.is_verified ? t('superadmin.users.deactivate', 'Deactivate') : t('superadmin.users.activate', 'Activate')}
                            title={admin.is_verified ? t('superadmin.users.deactivate', 'Deactivate') : t('superadmin.users.activate', 'Activate')}
                          >
                            {admin.is_verified ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteUser(admin);
                            }}
                            disabled={!canDeleteAdmin}
                            className={`p-1.5 rounded-lg ${
                              canDeleteAdmin
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            aria-label={t('superadmin.users.delete', 'Delete')}
                            title={canDeleteAdmin
                              ? t('superadmin.users.delete', 'Delete')
                              : t('superadmin.users.deleteBlocked', 'Remove RH users before deleting this admin')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-gray-200 px-4 py-3 bg-gray-50"
                        >
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                            {t('superadmin.users.rhUsersTitle', 'HR users')}
                          </div>

                          {rhUsers.length === 0 ? (
                            <div className="text-sm text-gray-500 py-2">
                              {t('superadmin.users.noRhUsers', 'No HR users linked to this admin')}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {rhUsers.map((rh) => (
                                <div
                                  key={rh.id}
                                  className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                                >
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{rh.name || rh.email}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {rh.email} • {formatDate(rh.created_at)}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleUserStatus(rh)}
                                      className={`p-1.5 rounded-lg ${
                                        rh.is_verified
                                          ? 'text-emerald-600 hover:bg-emerald-50'
                                          : 'text-red-600 hover:bg-red-50'
                                      }`}
                                      aria-label={rh.is_verified ? t('superadmin.users.deactivate', 'Deactivate') : t('superadmin.users.activate', 'Activate')}
                                      title={rh.is_verified ? t('superadmin.users.deactivate', 'Deactivate') : t('superadmin.users.activate', 'Activate')}
                                    >
                                      {rh.is_verified ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteUser(rh)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                      aria-label={t('superadmin.users.delete', 'Delete')}
                                      title={t('superadmin.users.delete', 'Delete')}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default SuperAdminUsers;
